import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, ChevronDown, ChevronUp, ArrowUpRight, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { useUser } from '../context/UserContext';
import { fmtCurrency } from '../utils';
import T from './crmTheme';
function CompanyDirectory({}) {
  const { user } = useUser();
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [companyStats, setCompanyStats] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', sector: '', phone: '', website: '', address_line1: '', address_line2: '', city: '', county: '', postcode: '', crm_notes: '' });
  const [savingId, setSavingId] = useState(null);

  const canEdit = ['bd', 'manager', 'director'].includes(user?.role);

  useEffect(() => {
    loadCompanies();
  }, []);

  async function loadCompanies() {
    setLoading(true);
    try {
      const data = await api.crmCompanies(search || undefined);
      setCompanies(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  useEffect(() => {
    const t = setTimeout(loadCompanies, 300);
    return () => clearTimeout(t);
  }, [search]);

  async function toggleExpand(id) {
    if (expanded === id) { setExpanded(null); return; }
    setExpanded(id);
    // Fetch contacts
    try {
      const contactData = await api.crmContacts({ company_id: id });
      setContacts(contactData);
    } catch (e) { setContacts([]); }
    // Fetch stats independently so a contacts error doesn't kill stats
    try {
      const statsRes = await api.get(`/api/crm/company-stats/${id}`);
      setCompanyStats(prev => ({ ...prev, [id]: statsRes || { total_tenders:0, won:0, lost:0, value_won:0, value_bid:0, avg_value:0, last_tender_at:null } }));
    } catch (e) {
      setCompanyStats(prev => ({ ...prev, [id]: { total_tenders:0, won:0, lost:0, value_won:0, value_bid:0, avg_value:0, last_tender_at:null } }));
    }
  }

  function startEdit(co) {
    setEditing(co.id);
    setEditForm({
      name: co.name || '', sector: co.sector || '',
      phone: co.phone || '', website: co.website || '',
      address_line1: co.address_line1 || '', address_line2: co.address_line2 || '',
      city: co.city || '', county: co.county || '', postcode: co.postcode || '',
      crm_notes: co.crm_notes || '',
    });
  }

  async function saveEdit(id) {
    setSavingId(id);
    try {
      await api.crmUpdateCompany(id, editForm);
      setEditing(null);
      loadCompanies();
    } catch (e) { alert(e.message || 'Save failed'); }
    setSavingId(null);
  }

  async function saveAdd() {
    if (!addForm.name.trim()) return alert('Company name is required');
    setSavingId('new');
    try {
      await api.crmCreateCompany(addForm);
      setShowAdd(false);
      setAddForm({ name: '', sector: '', phone: '', website: '', address_line1: '', address_line2: '', city: '', county: '', postcode: '', crm_notes: '' });
      loadCompanies();
    } catch (e) { alert(e.message || 'Create failed'); }
    setSavingId(null);
  }

  async function toggleActive(co) {
    if (!window.confirm(`${co.is_active ? 'Deactivate' : 'Reactivate'} "${co.name}"?`)) return;
    try {
      await api.patch(`/api/crm/companies/${co.id}/deactivate`, { active: !co.is_active });
      loadCompanies();
    } catch(e) { alert(e.message || 'Failed'); }
  }

  async function hardDelete(co) {
    if (!window.confirm(`Permanently delete "${co.name}"? This cannot be undone.`)) return;
    try {
      await api.delete ? api.delete(`/api/crm/companies/${co.id}`) : fetch(`/api/crm/companies/${co.id}`, { method:'DELETE', headers:{ Authorization:`Bearer ${localStorage.getItem('ipe_token')}` } }).then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error); });
      loadCompanies();
    } catch(e) { alert(e.message || 'Delete failed'); }
  }

  const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 12, fontFamily: 'inherit', outline: 'none' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..." style={{ ...inputStyle, maxWidth: 320 }} />
        <span style={{ color: T.textMuted, fontSize: 12 }}>{companies.length} companies</span>
        {canEdit && <button onClick={() => setShowAdd(!showAdd)} style={{ marginLeft: 'auto', padding: '7px 16px', borderRadius: 6, border: 'none', background: T.teal, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Company</button>}
      </div>

      {showAdd && (
        <div style={{ background: T.surface, border: `1px solid ${T.teal}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: T.teal }}>New Company</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Name *</label><input value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>City</label><input value={addForm.city} onChange={e => setAddForm(p => ({ ...p, city: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Sector</label><input value={addForm.sector} onChange={e => setAddForm(p => ({ ...p, sector: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Phone</label><input value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Website</label><input value={addForm.website} onChange={e => setAddForm(p => ({ ...p, website: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Postcode</label><input value={addForm.postcode} onChange={e => setAddForm(p => ({ ...p, postcode: e.target.value }))} style={inputStyle} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Address Line 1</label><input value={addForm.address_line1} onChange={e => setAddForm(p => ({ ...p, address_line1: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>City</label><input value={addForm.city} onChange={e => setAddForm(p => ({ ...p, city: e.target.value }))} style={inputStyle} /></div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Notes</label>
            <textarea value={addForm.crm_notes} onChange={e => setAddForm(p => ({ ...p, crm_notes: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAdd(false)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={saveAdd} disabled={savingId === 'new'} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: T.teal, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: savingId === 'new' ? 0.6 : 1 }}>{savingId === 'new' ? 'Saving...' : 'Create Company'}</button>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.textMuted, padding: 20 }}>Loading...</p> : (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 140px', padding: '10px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <span>Company</span><span>Sector</span><span>Phone</span><span>City</span><span>Postcode</span><span>Actions</span>
          </div>
          {companies.length === 0 && <p style={{ color: T.textMuted, padding: 20, textAlign: 'center', fontSize: 12 }}>No companies found</p>}
          {companies.map(co => (
            <div key={co.id}>
              {editing === co.id ? (
                <div style={{ padding: 14, borderBottom: `1px solid ${T.border}`, background: T.surface2 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 80px', gap: 8, alignItems: 'center' }}>
                    <input value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
                    <input value={editForm.sector} onChange={e => setEditForm(p => ({ ...p, sector: e.target.value }))} style={inputStyle} placeholder="Sector" />
                    <input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} style={inputStyle} placeholder="Phone" />
                    <input value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} style={inputStyle} placeholder="City" />
                    <input value={editForm.postcode} onChange={e => setEditForm(p => ({ ...p, postcode: e.target.value }))} style={inputStyle} placeholder="Postcode" />
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => saveEdit(co.id)} disabled={savingId === co.id} style={{ padding: '4px 8px', borderRadius: 4, border: 'none', background: T.teal, color: '#fff', fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>✓</button>
                      <button onClick={() => setEditing(null)} style={{ padding: '4px 8px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
                    <div><label style={{ fontSize: 10, color: T.textDim }}>Address</label><input value={editForm.address_line1} onChange={e => setEditForm(p => ({ ...p, address_line1: e.target.value }))} style={inputStyle} placeholder="Address line 1" /></div>
                    <div><label style={{ fontSize: 10, color: T.textDim }}>City</label><input value={editForm.city} onChange={e => setEditForm(p => ({ ...p, city: e.target.value }))} style={inputStyle} placeholder="City" /></div>
                    <div><label style={{ fontSize: 10, color: T.textDim }}>Address Line 2</label><input value={editForm.address_line2} onChange={e => setEditForm(p => ({ ...p, address_line2: e.target.value }))} style={inputStyle} placeholder="Address line 2" /></div>
                    <div><label style={{ fontSize: 10, color: T.textDim }}>Website</label><input value={editForm.website} onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))} style={inputStyle} placeholder="Website" /></div>
                  </div>
                  <div style={{ marginTop: 8 }}><label style={{ fontSize: 10, color: T.textDim }}>Notes</label><textarea value={editForm.crm_notes} onChange={e => setEditForm(p => ({ ...p, crm_notes: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                </div>
              ) : (
                <div onClick={() => toggleExpand(co.id)} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 140px', padding: '10px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 12, color: co.is_active === false ? T.textDim : T.text, cursor: 'pointer', transition: 'background 0.1s', alignItems: 'center', opacity: co.is_active === false ? 0.55 : 1 }} onMouseEnter={e => e.currentTarget.style.background = T.surface2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontWeight: 500, display:'flex', alignItems:'center', gap:6 }}>
                    {expanded === co.id ? '▾' : '▸'} {co.name}
                    {co.is_active === false && <span style={{ fontSize:9, fontWeight:700, color:'#f87171', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:3, padding:'1px 5px', textTransform:'uppercase', letterSpacing:0.5 }}>Inactive</span>}
                  </span>
                  <span style={{ color: T.textMuted }}>{co.sector || '—'}</span>
                  <span style={{ color: T.textMuted }}>{co.phone || '—'}</span>
                  <span style={{ color: T.textMuted }}>{co.city || '—'}</span>
                  <span style={{ color: T.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{co.postcode || '—'}</span>
                  <span onClick={e => e.stopPropagation()}>
                    {canEdit && <button onClick={() => startEdit(co)} style={{ padding: '3px 10px', borderRadius: 4, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 10, cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>}
                  </span>
                </div>
              )}
              {expanded === co.id && (
                <div style={{ padding: '12px 16px 12px 32px', borderBottom: `1px solid ${T.border}`, background: T.surface2 }}>
                  {co.address_line1 && <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>📍 {[co.address_line1, co.address_line2, co.city, co.county, co.postcode].filter(Boolean).join(', ')}</div>}
                  {co.website && <div style={{ fontSize: 11, color: T.teal, marginBottom: 6 }}>🌐 {co.website}</div>}
                  {co.phone && <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>📞 {co.phone}</div>}
                  {co.crm_notes && <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, fontStyle: 'italic' }}>📝 {co.crm_notes}</div>}
                  {(companyStats[co.id] != null) && (() => {
                    const s = companyStats[co.id];
                    const decided = (Number(s.won)||0) + (Number(s.lost)||0);
                    const winRate = decided > 0 ? Math.round((Number(s.won) / decided) * 100) : null;
                    const fmtGBP = v => v >= 1000000 ? `£${(v/1000000).toFixed(1)}m` : v >= 1000 ? `£${Math.round(v/1000)}k` : `£${Number(v).toLocaleString()}`;
                    return (
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:8, marginBottom:14, padding:'10px 12px', background:T.surface, borderRadius:8, border:`1px solid ${T.border}` }}>
                        {[
                          { label:'Tenders', value: s.total_tenders },
                          { label:'Won', value: s.won, color:'#10b981' },
                          { label:'Lost', value: s.lost, color:'#ef4444' },
                          { label:'Win Rate', value: winRate !== null ? `${winRate}%` : '—', color: winRate >= 50 ? '#10b981' : winRate !== null ? '#f87171' : T.textMuted },
                          { label:'Value Won', value: fmtGBP(s.value_won), color:'#10b981' },
                          { label:'Last Tender', value: s.last_tender_at ? new Date(s.last_tender_at).toLocaleDateString('en-GB',{month:'short',year:'numeric'}) : '—' },
                        ].map(st => (
                          <div key={st.label} style={{ textAlign:'center' }}>
                            <div style={{ fontSize:14, fontWeight:700, color: st.color || T.text }}>{st.value}</div>
                            <div style={{ fontSize:9, color:T.textMuted, textTransform:'uppercase', letterSpacing:'0.04em', marginTop:2 }}>{st.label}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {canEdit && (
                    <div style={{ display:'flex', gap:8, marginBottom:14, paddingBottom:12, borderBottom:`1px solid ${T.border}` }}>
                      <button onClick={() => toggleActive(co)} style={{ padding:'5px 14px', borderRadius:5, border:`1px solid ${co.is_active === false ? 'rgba(16,185,129,0.4)' : 'rgba(239,68,68,0.3)'}`, background:'transparent', color: co.is_active === false ? '#10b981' : '#f87171', fontSize:11, cursor:'pointer', fontFamily:'inherit', fontWeight:500 }}>
                        {co.is_active === false ? '✓ Reactivate Company' : '⊘ Deactivate Company'}
                      </button>
                      {user?.role === 'director' && co.is_active === false && (
                        <button onClick={() => hardDelete(co)} style={{ padding:'5px 14px', borderRadius:5, border:'1px solid rgba(239,68,68,0.5)', background:'rgba(239,68,68,0.08)', color:'#f87171', fontSize:11, cursor:'pointer', fontFamily:'inherit', fontWeight:500 }}>
                          🗑 Permanently Delete
                        </button>
                      )}
                    </div>
                  )}
                  <div style={{ fontWeight: 600, fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Contacts ({contacts.length})</div>
                  {contacts.length === 0 ? <p style={{ color: T.textDim, fontSize: 11 }}>No contacts for this company</p> : (
                    <div style={{ display: 'grid', gap: 6 }}>
                      {contacts.map(ct => (
                        <div key={ct.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 10px', background: T.surface, borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 11 }}>
                          <div style={{ width: 26, height: 26, borderRadius: '50%', background: T.tealDim, border: `1px solid ${T.tealGlow}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: T.teal }}>{(ct.first_name?.[0] || '') + (ct.last_name?.[0] || '')}</div>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 500, color: T.text }}>{ct.first_name} {ct.last_name}</span>
                            {ct.job_title && <span style={{ color: T.textDim, marginLeft: 6 }}>— {ct.job_title}</span>}
                          </div>
                          {ct.email && <span style={{ color: T.textMuted }}>{ct.email}</span>}
                          {ct.phone && <span style={{ color: T.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 10 }}>{ct.phone_country_code || '+44'} {ct.phone}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// CONTACT DIRECTORY
// ═══════════════════════════════════════════════════════════════════════════════


export default CompanyDirectory;
